import { consume } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { BreadcrumbItem } from '../../design-system/breadcrumb/breadcrumbs';
import '../../design-system/button/button';
import { i18n, TranslationKey } from '../../i18n/i18n';
import '../../shared/components/completion-bar/completion-bar';
import { CompletionBarVariant } from '../../shared/components/completion-bar/completion-bar';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsElevation } from '../../shared/styles/elevation';
import { ludsHeadingXS } from '../../shared/styles/typography';
import { ComponentTypes, ENROLLMENT_STATUSES } from '../../shared/types/common.model';
import { CoursePlayerState, stateContext } from './course-player.context';
import { DESCRIPTION_ROUTE } from './routes';

@customElement('lua-course-player-header')
export class LuaCoursePlayerHeader extends LitElement {
  static override styles = [
    resetStyle,
    ludsHeadingXS,
    ludsElevation,
    css`
      :host {
        --lua-course-player-header-background-color: var(--surfaces-surface-light);
        --lua-course-player-header-padding: var(--spacing-spacing-16) var(--spacing-spacing-24);
        --lua-course-player-header-color: var(--object-object-primary);

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        background-color: var(--lua-course-player-header-background-color);
        padding: 0;
        color: var(--lua-course-player-header-color);
      }

      .header-content {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;
        padding: var(--lua-course-player-header-padding);
        min-height: 52px;
      }

      .header-content luds-breadcrumbs {
        flex: 1;
      }

      .header-content__title {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .sidebar-toggle {
        display: none;
      }

      @media (max-width: 768px) {
        .exit-label {
          display: none;
        }

        .sidebar-toggle {
          display: inline-block;
        }

        .header-content {
          gap: var(--spacing-spacing-12);
        }

        .header-content__title {
          text-align: center;
          flex-grow: 1;
        }
      }
    `,
  ];

  @consume({ context: stateContext, subscribe: true })
  private coursePlayerState: CoursePlayerState;

  private backToCourseHandler() {
    this.coursePlayerState.navigateBackToCourse();
  }

  private editThisModuleHandler() {
    this.coursePlayerState.navigateToEditContent();
  }

  private seeMoreHandler() {
    this.coursePlayerState.navigateToCompletePage();
  }

  private leaveModuleHandler() {
    this.coursePlayerState.leaveModule();
  }

  private getCompletionStatus() {
    const status = this.coursePlayerState?.enrollment?.status_id;

    const map: Partial<{
      [key in ENROLLMENT_STATUSES]: { text: TranslationKey; variant: CompletionBarVariant };
    }> = {
      [ENROLLMENT_STATUSES.FAILED]: {
        text: 'lua.courses.completion_bar.unsuccessful',
        variant: 'unsuccessful',
      },
      [ENROLLMENT_STATUSES.PASSED]: {
        text: 'lua.courses.completion_bar.successful',
        variant: 'congratulations',
      },
      [ENROLLMENT_STATUSES.COMPLETED]: {
        text: 'lua.courses.completion_bar.successful',
        variant: 'congratulations',
      },
    };

    return status in map && map[`${status}`];
  }

  private getModuleTypeTranslationKey() {
    const componentTypeId = this.coursePlayerState?.currentModule?.component_type_id;

    const translationKeysByComponentType: Partial<{
      [key in ComponentTypes]: TranslationKey;
    }> = {
      [ComponentTypes.exam]: this.getExamTitleVariation(),
      [ComponentTypes.scorm]: 'lua.component_type.module',
      [ComponentTypes.page]: 'lua.component_type.module',
      [ComponentTypes.assignment]: 'lua.component_type.assignment',
      [ComponentTypes.session]: 'lua.component_type.session',
      [ComponentTypes.training]: 'lua.component_type.training',
      [ComponentTypes.checklist]: 'lua.component_type.checklist',
      [ComponentTypes.tin_can]: 'lua.component_type.tin_can',
      [ComponentTypes.e_signature]: 'lua.component_type.e_signature',
    };

    return translationKeysByComponentType[componentTypeId as ComponentTypes] || 'lua.component_type.module';
  }

  private getExamTitleVariation() {
    const isSurvey = this.coursePlayerState?.currentModule?.is_survey;
    const isKnowledgeTest = this.coursePlayerState?.currentModule?.is_knowledge_test;

    if (isSurvey) {
      return 'lua.component_type.survey';
    }

    if (isKnowledgeTest) {
      return 'lua.component_type.knowledge_test';
    }

    return 'lua.component_type.exam';
  }

  // Helper function to render buttons based on course player state
  private renderButtons() {
    const moduleType = i18n(this.getModuleTypeTranslationKey());
    const { isPreview, currentModule, showExitModuleButton } = this.coursePlayerState;

    if (isPreview && !currentModule) {
      return html`<luds-button variant="secondary" @click=${this.backToCourseHandler} data-qa-id="Back to course"
        >${i18n('lua.header.back_to_course')}</luds-button
      >`;
    } else if (isPreview) {
      return html`
        <luds-button variant="secondary" @click=${this.backToCourseHandler} data-qa-id="Back to course"
          >${i18n('lua.header.back_to_course')}</luds-button
        >
        <luds-button variant="primary" @click=${this.editThisModuleHandler} data-qa-id="Edit this module"
          >${i18n('lua.header.edit_this_module')}</luds-button
        >
      `;
    } else if (showExitModuleButton) {
      return html`
        <luds-button variant="secondary" @click=${this.leaveModuleHandler} data-qa-id="Leave"
          >${i18n('lua.survey.leave_module_cta')} ${moduleType}</luds-button
        >
      `;
    }
  }

  private getHeader(): string {
    const moduleType = i18n(this.getModuleTypeTranslationKey());
    const currentRoute = this.coursePlayerState?.currentRoute;

    if (currentRoute === DESCRIPTION_ROUTE) {
      return i18n('lua.courses.about').toString();
    }

    return `${moduleType}: ${this.coursePlayerState?.currentModule?.title}`;
  }

  private getBreadcrumbLinks(): BreadcrumbItem[] {
    const breadcrumbItems: BreadcrumbItem[] = [];

    if (this.coursePlayerState?.enrollment) {
      if (this.coursePlayerState?.learningJourneyContext) {
        breadcrumbItems.push(this.getLearningJourneyPathSection());
      }
      breadcrumbItems.push(this.getCoursePathSection());
      breadcrumbItems.push(this.getModulePathSection());
    }

    return breadcrumbItems;
  }

  private getHomePathSection(): BreadcrumbItem {
    const { learningJourneyContext } = this.coursePlayerState ?? {};
    if (learningJourneyContext) {
      const { path, itemName } = this.getLearningJourneyPathSection();
      return { id: 1, path, itemName };
    }

    return {
      id: 1,
      path: '#',
      onClick: () => {
        this.coursePlayerState.exitCourse();
      },
      itemName: 'Home',
    };
  }

  private getLearningJourneyPathSection(): BreadcrumbItem {
    const { external_enrollment_id, external_workflow_id, external_version_id, name } =
      this.coursePlayerState.learningJourneyContext;
    const { user_id } = this.coursePlayerState.enrollment;

    return {
      path: `/learning-journey-progression/${external_workflow_id}/${external_version_id}/${user_id}/${external_enrollment_id}`,
      id: 2,
      itemName: name,
    };
  }

  private getCoursePathSection(): BreadcrumbItem {
    const { id, name } = this.coursePlayerState.enrollment;
    const { isPreview } = this.coursePlayerState;
    const isContentDetailsRedesignEnabled = this.coursePlayerState.featureFlags.content_details_redesign_enabled;

    switch (true) {
      case isPreview:
        return {
          path: '#',
          id: 3,
          itemName: name,
          onClick: () => {
            this.coursePlayerState.navigateBackToCourse();
          },
        };
      case isContentDetailsRedesignEnabled:
        return {
          path: `/content-details/${id}/2`,
          id: 3,
          itemName: name,
        };
      default:
        return {
          path: `/enrollments/${id}/details`,
          id: 3,
          itemName: name,
        };
    }
  }

  private getModulePathSection(): BreadcrumbItem {
    const headerTitle = this.getHeader();

    return {
      itemName: headerTitle,
      id: 4,
    };
  }

  private toggleSideNav(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.coursePlayerState.toggleSidenav();
  }

  private renderBreadcrumbs() {
    const breadcrumbItems: BreadcrumbItem[] = this.getBreadcrumbLinks();
    const breadcrumbHomeItem: BreadcrumbItem = this.getHomePathSection();

    // Hide breadcrumbs in LUA mode
    const { showExitCourseButton, isTakingExam } = this.coursePlayerState;
    return showExitCourseButton && !isTakingExam
      ? html`<luds-breadcrumbs .items=${breadcrumbItems} .homeItem=${breadcrumbHomeItem}></luds-breadcrumbs>`
      : html`<div></div>`;
  }

  override render() {
    const { variant, text } = this.getCompletionStatus();

    return html`
      <div class="header-content">
        <luds-button
          class="sidebar-toggle"
          variant="secondary"
          size="small"
          icon="sidebar"
          aria-label=${i18n('lua.a11y.toggle_side_navigation')}
          @click="${this.toggleSideNav}"></luds-button>
        ${this.renderBreadcrumbs()}
        <nav>${this.renderButtons()}</nav>
      </div>
      ${this.coursePlayerState.canShowCompletionBar()
        ? html`<lua-completion-bar .variant="${variant}" @lua-completion-bar:see-more-clicked=${this.seeMoreHandler}
            >${i18n(text)}</lua-completion-bar
          >`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lua-course-player-header': LuaCoursePlayerHeader;
  }
}
